import { api } from '@/core/plugins/axios';
import { ApiResponse } from '@/shared/types/api';

import { HouseholdInvitation, HouseholdInvitationWithHouseholdName } from '@/features/household/models/invitation';
import {
    AcceptHouseholdInvitationDTO,
    DeclineHouseholdInvitationDTO,
    HouseholdInvitationsDTO,
    HouseholdInvitationDTO,
    HouseholdInvitationWithHouseholdNameDTO,
    RevokeHouseholdInvitationDTO
} from '@/features/household/dto/invitation_dto';
import {
    CreateHouseholdInvitationForm,
    AcceptHouseholdInvitationForm,
    DeclineHouseholdInvitationForm,
    RevokeHouseholdInvitationForm
} from '@/features/household/form/invitation_form';
import {
    fromHouseholdInvitationDTO,
    fromHouseholdInvitationWithHouseholdNameDTO,
    toCreateHouseholdInvitationDTO,
    toAcceptHouseholdInvitationDTO,
    toDeclineHouseholdInvitationDTO,
    toRevokeHouseholdInvitationDTO
} from '@/features/household/mappers/invitation_mapper';

const BASE_URL = 'api/v1/households/invitations';

async function getPendingInvitation(): Promise<ApiResponse<HouseholdInvitationWithHouseholdName>> {
    try {
        const response = await api.get<HouseholdInvitationWithHouseholdNameDTO>(`${BASE_URL}/me`);
        const domainInvitation = fromHouseholdInvitationWithHouseholdNameDTO(response.data);

        return {
            status: 'success',
            data: domainInvitation,
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error',
            },
        };
    }
}

async function getInvitationByToken(token: string): Promise<ApiResponse<HouseholdInvitationWithHouseholdName>> {
    try {
        const response = await api.get<HouseholdInvitationWithHouseholdNameDTO>(`${BASE_URL}/token/${token}`);
        const domainInvitation = fromHouseholdInvitationWithHouseholdNameDTO(response.data);

        return {
            status: 'success',
            data: domainInvitation, 
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error',
            },
        };
    }
}

async function getHouseholdInvitations(): Promise<ApiResponse<HouseholdInvitation[]>> {
    try {
        const response = await api.get<HouseholdInvitationsDTO>(`${BASE_URL}`);
        const dtoArray = response.data.invitations ?? [];
  
        const domainInvitations = dtoArray.map(fromHouseholdInvitationDTO);
    
        return {
            status: 'success',
            data: domainInvitations,
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error',
            },
        };
    }
}

async function createHouseholdInvitation(form: CreateHouseholdInvitationForm): Promise<ApiResponse<HouseholdInvitation>> {
    try {
        const dto = toCreateHouseholdInvitationDTO(form);
        const response = await api.post<HouseholdInvitationDTO>(`${BASE_URL}`, dto);

        if (response.status === 204) {
            return {
                status: 'error',
                error: {
                    code: 204,
                    message: 'No content returned from server',
                },
            };
        }
        const createdInvitation = fromHouseholdInvitationDTO(response.data);
        return {
            status: 'success',
            data: createdInvitation,
        };
        } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Failed to create invitation',
            },
        };
    }
}

async function revokeHouseholdInvitation(form: RevokeHouseholdInvitationForm): Promise<ApiResponse<null>> {
    try {
        const dto = toRevokeHouseholdInvitationDTO(form);
        const response = await api.post<RevokeHouseholdInvitationDTO>(`${BASE_URL}/revoke`, dto);
        if (response.status === 204) {
            return {
                status: 'success',
                data: null
            };
        }
        return {
            status: 'error',
            error: {
                code: 500,
                message: 'No content returned from server',
            },
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Failed to revoke invitation',
            },
        };
    }
}

async function acceptHouseholdInvitation(form: AcceptHouseholdInvitationForm): Promise<ApiResponse<null>> {
    try {
        const dto = toAcceptHouseholdInvitationDTO(form);
        const response = await api.post<AcceptHouseholdInvitationDTO>(`${BASE_URL}/accept`, dto);
        if (response.status === 204) {
            return {
                status: 'success',
                data: null
            };
        }
        return {
            status: 'error',
            error: {
                code: 500,
                message: 'No content returned from server',
            },
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error',
            },
        };
    }
}

async function declineHouseholdInvitation(form: DeclineHouseholdInvitationForm): Promise<ApiResponse<null>> {
    try {
        const dto = toDeclineHouseholdInvitationDTO(form);
        const response = await api.post<DeclineHouseholdInvitationDTO>(`${BASE_URL}/decline`, dto);
        if (response.status === 204) {
            return {
                status: 'success',
                data: null
            };
        }
        return {
            status: 'error',
            error: {
                code: 500,
                message: 'No content returned from server',
            },
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error',
            },
        };
    }
}

export default {
    getPendingInvitation,
    getInvitationByToken,
    getHouseholdInvitations,
    createHouseholdInvitation,
    revokeHouseholdInvitation,
    acceptHouseholdInvitation,
    declineHouseholdInvitation,
}
